/**
 * EventTrailMap — collapsible trail-map panel that lives INSIDE the
 * Video Player Modal. Renders the GPS trail for ±N minutes around the
 * event without leaving the modal.
 *
 * Reuses existing infrastructure:
 *   • Backend endpoint  GET /api/gps/history/{device_id}?start_time&end_time
 *     (already tenant-isolated via assert_device_access)
 *   • Leaflet + react-leaflet AND Google Maps (mirrors Live Tracking)
 *   • EventMarkerIcon shield "E" SVG (visual parity with Live Tracking)
 *   • LiveTrackingContext for map type / dark mode preference
 *   • timezone.js helpers for all time formatting (no raw toLocaleString)
 *
 * No new backend endpoints, no DB schema, no new feature flags.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker as LeafletMarker, Polyline as LeafletPolyline, CircleMarker as LeafletCircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap as useGoogleMap } from '@vis.gl/react-google-maps';
import { ChevronDown, ChevronUp, Loader2, Maximize2, Minimize2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveTracking } from '../../contexts/LiveTrackingContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { resolveTimezone, formatTimeOnly, formatSpeed } from '../../utils/timezone';
import { getPreferredMapType } from '../../utils/mapPreferences';
import { EventMarkerIcon } from '../events/EventMapMarker';
import { buildLeafletArrowIcon, buildGoogleArrowIcon } from './TrailCursorArrow';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;

// ── Speed → colour gradient (blue slow → green cruise → amber → red over-limit) ──
const speedToColor = (speedKmh) => {
  const s = Math.max(0, Number(speedKmh) || 0);
  if (s < 5) return '#3b82f6';
  if (s < 30) return '#22c55e';
  if (s < 60) return '#eab308';
  if (s < 100) return '#f97316';
  return '#ef4444';
};

// ── Tile layer picker for Leaflet (dark mode / terrain / satellite / standard) ──
const pickTileLayer = (mapType, darkModeMap) => {
  if (darkModeMap) {
    return {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; OpenStreetMap, Tiles &copy; Esri',
      subdomains: '',
    };
  }
  switch (mapType) {
    case 'satellite':
    case 'hybrid':
      return {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri',
      };
    case 'terrain':
      return {
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap, &copy; OpenTopoMap',
        subdomains: 'abc',
      };
    case 'osm':
    case 'roadmap':
    default:
      return {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap',
        subdomains: 'abc',
      };
  }
};

// ── Shield "E" icon for Leaflet (mirrors LiveTracking marker) ──
// NOTE: the decorative pulsing ring was REMOVED — its 44px hit area blocked
// clicks on trail data points sitting close to the [E]. The shield itself
// is visually distinct enough; pulse is only used in LiveTracking where the
// alert needs to grab the operator's attention.
const buildLeafletEventIcon = (severity = 'warning', label = 'E') => {
  const shieldColor = severity === 'critical' ? '#dc2626' : severity === 'info' ? '#2563eb' : '#f59e0b';
  const safeLabel = String(label || 'E').slice(0, 3).replace(/[<>"'&]/g, '');
  return L.divIcon({
    className: '',
    html: `<div style="width:0;height:0;overflow:visible;cursor:pointer" data-testid="event-trail-map-marker">
        <div style="position:absolute;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 6px rgba(0,0,0,.4));z-index:1">
          <svg width="30" height="30" viewBox="0 0 514 512" xmlns="http://www.w3.org/2000/svg">
            <path fill="${shieldColor}" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/>
          </svg>
          <div style="position:absolute;left:50%;top:38%;transform:translate(-50%,-50%);font:700 9px/1 Arial,sans-serif;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.65);max-width:24px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeLabel}</div>
        </div>
      </div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
};

// ── Leaflet: fit bounds when trail arrives ──
const LeafletFitBounds = ({ points, focusLat, focusLng }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) {
      if (focusLat != null && focusLng != null) map.setView([focusLat, focusLng], 17);
      return;
    }
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    if (focusLat != null && focusLng != null) bounds.extend([focusLat, focusLng]);
    map.fitBounds(bounds, { padding: [25, 25], maxZoom: 18 });
  }, [points, focusLat, focusLng, map]);
  return null;
};

// ── Google: fit bounds + create native Marker (pixel-scaled) for trail ──
//
// IMPORTANT: ``google.maps.Circle`` takes radius in METRES, which renders
// enormously at street zoom (~6 m ≈ a 30 px blob at zoom 17 and the entire
// screen at zoom 20). We therefore use ``google.maps.Marker`` with a
// ``SymbolPath.CIRCLE`` icon whose ``scale`` is in PIXELS — constant
// visual size regardless of map zoom. This matches the Leaflet
// ``CircleMarker`` behaviour on the OSM path.
const GoogleTrailLayer = ({ points, focusLat, focusLng, cursorPoint, onHover }) => {
  const map = useGoogleMap();
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const cursorRef = useRef(null);

  // Fit bounds when trail data arrives
  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    if (!points || points.length === 0) {
      if (focusLat != null && focusLng != null) {
        map.setCenter({ lat: focusLat, lng: focusLng });
        map.setZoom(17);
      }
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (focusLat != null && focusLng != null) bounds.extend({ lat: focusLat, lng: focusLng });
    map.fitBounds(bounds, 30);
  }, [map, points, focusLat, focusLng]);

  // Draw the base polyline + speed-coloured PIXEL-sized markers
  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    // Tear down previous
    if (polylineRef.current) { polylineRef.current.setMap(null); polylineRef.current = null; }
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (!points || points.length === 0) return;

    // Base polyline (semi-transparent)
    polylineRef.current = new window.google.maps.Polyline({
      path: points.map(p => ({ lat: p.lat, lng: p.lng })),
      geodesic: false,
      strokeColor: '#60a5fa',
      strokeOpacity: 0.55,
      strokeWeight: 3,
      map,
      zIndex: 5,
    });

    // Speed-coloured pixel-sized dots (CIRCLE symbol, scale in pixels)
    markersRef.current = points.map((p) => {
      const color = speedToColor(p.speed);
      const marker = new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 4,           // RADIUS IN PIXELS — stays small at any zoom
          fillColor: color,
          fillOpacity: 0.95,
          strokeWeight: 0,
        },
        clickable: true,
        zIndex: 6,
      });
      marker.addListener('mouseover', () => onHover && onHover(p));
      marker.addListener('mouseout', () => onHover && onHover(null));
      return marker;
    });

    return () => {
      if (polylineRef.current) polylineRef.current.setMap(null);
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, points, onHover]);

  // Time-synced playback cursor — drawn as a heading-rotated arrow so the
  // recipient sees both WHERE the vehicle was AND WHICH WAY it was facing
  // at the current video frame. Hover on the cursor itself surfaces the
  // same readout card the small dots use (necessary because at a complete
  // stop the cursor sits directly on top of the underlying trail dot and
  // would otherwise eat its hover event).
  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    if (cursorRef.current) { cursorRef.current.setMap(null); cursorRef.current = null; }
    if (!cursorPoint) return;
    const icon = buildGoogleArrowIcon({
      heading: cursorPoint.heading || 0,
      color: speedToColor(cursorPoint.speed),
    });
    if (!icon) return;
    cursorRef.current = new window.google.maps.Marker({
      position: { lat: cursorPoint.lat, lng: cursorPoint.lng },
      map,
      icon,
      clickable: true,
      zIndex: 9999,
    });
    if (onHover) {
      cursorRef.current.addListener('mouseover', () => onHover(cursorPoint));
      cursorRef.current.addListener('mouseout', () => onHover(null));
    }
    return () => {
      if (cursorRef.current) cursorRef.current.setMap(null);
    };
  }, [map, cursorPoint, onHover]);

  return null;
};

// ── Trail window dropdown options ──
const WINDOW_CHOICES = [
  { value: 1, label: '± 1 min' },
  { value: 3, label: '± 3 min' },
  { value: 5, label: '± 5 min' },
];

// ── Find trail point closest to a target UTC instant ──
const findClosestTrailPoint = (points, targetUtcMs) => {
  if (!points || points.length === 0 || targetUtcMs == null) return null;
  let lo = 0, hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const midMs = new Date(pointTime(points[mid])).getTime();
    if (midMs < targetUtcMs) lo = mid + 1; else hi = mid;
  }
  const a = points[Math.max(0, lo - 1)];
  const b = points[lo];
  const aMs = new Date(pointTime(a)).getTime();
  const bMs = new Date(pointTime(b)).getTime();
  return Math.abs(aMs - targetUtcMs) <= Math.abs(bMs - targetUtcMs) ? a : b;
};

const pointTime = (point) => point?.route_timestamp || point?.position_timestamp || point?.timestamp || point?.server_timestamp;

const pointEventCodes = (point) => {
  const codes = new Set();
  const io = point?.io_elements || {};
  const nested = io?.elements || {};
  [point?.event_code, point?.event_type, point?.event_id, io?.event_id, io?.event_code_ext, nested?.event_code_ext]
    .filter(v => v !== undefined && v !== null && v !== '' && String(v) !== '0')
    .forEach(v => codes.add(String(v)));
  return codes;
};

const videoEventCodeSet = (video) => {
  const codes = new Set();
  (Array.isArray(video?.event_codes) ? video.event_codes : [])
    .filter(Boolean)
    .forEach(code => codes.add(String(code)));
  if (video?.event_type && String(video.event_type) !== '0') codes.add(String(video.event_type));
  return codes;
};

const eventLabelForPoint = (point, video) => {
  const pointCodes = pointEventCodes(point);
  const labels = Array.isArray(video?.event_labels) ? video.event_labels : [];
  const codes = Array.isArray(video?.event_codes) ? video.event_codes.map(String) : [];
  for (const code of pointCodes) {
    const idx = codes.indexOf(String(code));
    if (idx >= 0 && labels[idx]) return labels[idx];
  }
  return video?.event_label_display || video?.event_label || 'Event';
};

export const EventTrailMap = ({ video, device, currentVideoTime = 0, modalOpen = true }) => {
  const { api } = useAuth();
  const { mapType, darkModeMap, showPOI } = useLiveTracking();
  const { isFeatureEnabled, hasOSMMaps, hasGoogleMaps } = useFeatureFlags();

  // Privilege gate — show the trail panel only when the
  // ``media_video_show_trail_map`` privilege is enabled. Strict for all
  // roles (no super-admin bypass) so admins can verify the gate by
  // turning the flag off on themselves.
  const canShowTrailMap = isFeatureEnabled('media_video_show_trail_map');

  const [expanded, setExpanded] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(3);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoverPoint, setHoverPoint] = useState(null);

  const eventInstantIso = video?.media_start_time_utc || video?.event_time || video?.start_time || video?.created_at;
  const eventInstantMs = eventInstantIso ? new Date(eventInstantIso).getTime() : null;
  const eventEndIso = video?.media_end_time_utc || video?.end_time || null;
  const eventEndMs = eventEndIso
    ? new Date(eventEndIso).getTime()
    : eventInstantMs != null && video?.duration_seconds
      ? eventInstantMs + Number(video.duration_seconds) * 1000
      : eventInstantMs;
  const focusLat = video?.latitude != null ? Number(video.latitude) : null;
  const focusLng = video?.longitude != null ? Number(video.longitude) : null;
  const deviceId = video?.device_id || device?.id || null;
  const eventSeverity = useMemo(() => {
    const t = (video?.event_type || video?.event_label || '').toLowerCase();
    if (t.includes('impact') || t.includes('crash') || t.includes('collision') || t.includes('sos')) return 'critical';
    if (t.includes('info') || t.includes('record')) return 'info';
    return 'warning';
  }, [video]);
  const effectiveMapType = getPreferredMapType({ contextMapType: mapType, hasOSMMaps, hasGoogleMaps });
  const useGoogle = Boolean(GOOGLE_MAPS_API_KEY) && effectiveMapType !== 'osm';
  const tileCfg = useMemo(() => pickTileLayer(effectiveMapType, darkModeMap), [effectiveMapType, darkModeMap]);
  const deviceTz = resolveTimezone({ device });

  // Reset everything when the modal closes (the [E] marker only ever
  // shows while the modal is open — no fade, no leftover state).
  useEffect(() => {
    if (modalOpen) return;
    // Reset dialog-scoped state when the modal closes — correct effect usage
    // that react-hooks/set-state-in-effect over-flags.

    setExpanded(false);
    setMaximized(false);
    setPoints([]);
    setHoverPoint(null);
    setError(null);
  }, [modalOpen]);

  // Fetch trail when panel expands or window changes
  useEffect(() => {
    if (!expanded) return;
    if (!deviceId || !eventInstantMs) {

      setError('Event is missing a device or timestamp');
      return;
    }
    let cancelled = false;
    const start = new Date(eventInstantMs - windowMinutes * 60_000).toISOString();
    const end = new Date(eventInstantMs + windowMinutes * 60_000).toISOString();
    setLoading(true);
    setError(null);
    api.get(`/gps/history/${deviceId}`, { params: { start_time: start, end_time: end, limit: 2000 } })
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data?.points) ? res.data.points : [];
        data.sort((a, b) => new Date(pointTime(a)) - new Date(pointTime(b)));
        setPoints(data);
      })
      .catch(err => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to load trail');
        setPoints([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, deviceId, eventInstantMs, windowMinutes, api]);

  // Live cursor along the trail driven by the video's current playback time
  const cursorPoint = useMemo(() => {
    if (!points || points.length === 0 || eventInstantMs == null) return null;
    const targetUtcMs = eventInstantMs + (Number(currentVideoTime) || 0) * 1000;
    return findClosestTrailPoint(points, targetUtcMs);
  }, [points, eventInstantMs, currentVideoTime]);

  const eventTrailMarkers = useMemo(() => {
    if (!points?.length || eventInstantMs == null || eventEndMs == null) return [];
    const wantedCodes = videoEventCodeSet(video);
    const markers = [];
    const seen = new Set();
    for (const point of points) {
      const t = new Date(pointTime(point)).getTime();
      if (!Number.isFinite(t) || t < eventInstantMs || t > eventEndMs) continue;
      const codes = pointEventCodes(point);
      if (wantedCodes.size > 0 && ![...codes].some(code => wantedCodes.has(String(code)))) continue;
      if (codes.size === 0 && wantedCodes.size > 0) continue;
      const label = eventLabelForPoint(point, video);
      const key = `${pointTime(point)}-${point.lat}-${point.lng}-${[...codes].join(',')}`;
      if (seen.has(key)) continue;
      seen.add(key);
      markers.push({
        ...point,
        event_label: label,
        event_codes: [...codes],
        marker_label: [...codes][0] || 'E',
      });
    }
    return markers;
  }, [points, eventInstantMs, eventEndMs, video]);

  const fallbackLeafletEventIcon = useMemo(() => buildLeafletEventIcon(eventSeverity, 'E'), [eventSeverity]);

  if (!canShowTrailMap) return null;
  if (!focusLat || !focusLng || !deviceId || !eventInstantIso) return null;

  const mapHeight = maximized ? '70vh' : '420px';

  return (
    <div className="bg-slate-900" data-testid="event-trail-map-panel">
      {/* Toggle row (no border-t to remove the black band between video info and the map) */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-800">
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-300 hover:bg-slate-800 gap-2"
          onClick={() => setExpanded(v => !v)}
          data-testid="event-trail-map-toggle"
        >
          <MapPin className="w-4 h-4 text-blue-400" />
          {expanded ? 'Hide Trail Map' : 'Show Trail Map'}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded && points.length > 0 && (
            <span className="ml-2 text-[10px] text-slate-400" data-testid="event-trail-point-count">
              {points.length} pts · ±{windowMinutes}m
            </span>
          )}
        </Button>
        {expanded && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">Window</label>
            <select
              value={windowMinutes}
              onChange={(e) => setWindowMinutes(Number(e.target.value))}
              className="bg-slate-800 text-slate-200 text-xs rounded px-2 py-1 border border-slate-700"
              data-testid="event-trail-map-window-select"
            >
              {WINDOW_CHOICES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="icon"
              className="text-slate-300 hover:bg-slate-800 h-8 w-8"
              onClick={() => setMaximized(v => !v)}
              title={maximized ? 'Restore' : 'Maximise map'}
              data-testid="event-trail-map-maximize"
            >
              {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        )}
      </div>

      {/* Map */}
      {expanded && (
        <div className="relative" style={{ height: mapHeight }}>
          {useGoogle ? (
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                defaultCenter={{ lat: focusLat, lng: focusLng }}
                defaultZoom={17}
                mapId={showPOI ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
                colorScheme={darkModeMap ? 'DARK' : 'LIGHT'}
                mapTypeId={effectiveMapType}
                style={{ width: '100%', height: '100%' }}
                gestureHandling="greedy"
                disableDefaultUI={false}
                zoomControl={true}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
              >
                <GoogleTrailLayer
                  points={points}
                  focusLat={focusLat}
                  focusLng={focusLng}
                  cursorPoint={cursorPoint}
                  onHover={setHoverPoint}
                />
                {eventTrailMarkers.length > 0 ? eventTrailMarkers.map((marker, idx) => (
                  <AdvancedMarker key={`event-marker-${idx}`} position={{ lat: Number(marker.lat), lng: Number(marker.lng) }} zIndex={10000 + idx}>
                    <div onMouseEnter={() => setHoverPoint(marker)} onMouseLeave={() => setHoverPoint(null)} data-testid={`event-trail-exact-marker-${idx}`}>
                      <EventMarkerIcon severity={eventSeverity} pulse={false} />
                    </div>
                  </AdvancedMarker>
                )) : (
                  <AdvancedMarker position={{ lat: focusLat, lng: focusLng }} zIndex={10000}>
                    <EventMarkerIcon severity={eventSeverity} pulse={false} />
                  </AdvancedMarker>
                )}
              </GoogleMap>
            </APIProvider>
          ) : (
            <MapContainer
              center={[focusLat, focusLng]}
              zoom={17}
              scrollWheelZoom
              className="h-full w-full"
              style={{ background: darkModeMap ? '#0b1220' : '#f1f5f9' }}
            >
              <TileLayer url={tileCfg.url} attribution={tileCfg.attribution} subdomains={tileCfg.subdomains} />
              <LeafletFitBounds points={points} focusLat={focusLat} focusLng={focusLng} />

              {points.length >= 2 && (
                <LeafletPolyline
                  positions={points.map(p => [p.lat, p.lng])}
                  pathOptions={{ color: darkModeMap ? '#60a5fa' : '#2563eb', weight: 3, opacity: 0.45 }}
                />
              )}

              {points.map((p, i) => {
                const color = speedToColor(p.speed);
                return (
                  <LeafletCircleMarker
                    key={`trail-dot-${i}`}
                    center={[p.lat, p.lng]}
                    radius={4}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.95, weight: 0 }}
                    eventHandlers={{
                      mouseover: () => setHoverPoint(p),
                      mouseout: () => setHoverPoint(null),
                    }}
                  />
                );
              })}

              {cursorPoint && (
                <LeafletMarker
                  position={[cursorPoint.lat, cursorPoint.lng]}
                  icon={buildLeafletArrowIcon({
                    heading: cursorPoint.heading || 0,
                    color: speedToColor(cursorPoint.speed),
                  })}
                  zIndexOffset={5000}
                  eventHandlers={{
                    mouseover: () => setHoverPoint(cursorPoint),
                    mouseout: () => setHoverPoint(null),
                  }}
                />
              )}

              {eventTrailMarkers.length > 0 ? eventTrailMarkers.map((marker, idx) => (
                <LeafletMarker
                  key={`event-trail-exact-marker-${idx}`}
                  position={[Number(marker.lat), Number(marker.lng)]}
                  icon={buildLeafletEventIcon(eventSeverity, marker.marker_label)}
                  zIndexOffset={9999 + idx}
                  eventHandlers={{
                    mouseover: () => setHoverPoint(marker),
                    mouseout: () => setHoverPoint(null),
                  }}
                />
              )) : (
                <LeafletMarker
                  position={[focusLat, focusLng]}
                  icon={fallbackLeafletEventIcon}
                  zIndexOffset={9999}
                />
              )}
            </MapContainer>
          )}

          {/* Hover readout */}
          {hoverPoint && (
            <div
              className="absolute top-3 left-3 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 pointer-events-none shadow-lg max-w-[280px] z-[1000]"
              data-testid="event-trail-hover-readout"
            >
              <div className="font-medium text-blue-300">
                {hoverPoint.event_label && (
                  <span className="mr-2 text-amber-300">{hoverPoint.event_label}</span>
                )}
                {/* Trail point timestamps come from device_data and are
                 *  ALWAYS stored as UTC ISO regardless of how the parent
                 *  event_time was stamped. Format in the device's
                 *  configured timezone so the reader sees local time. */}
                {formatTimeOnly(pointTime(hoverPoint), { device, seconds: true })}
              </div>
              <div className="mt-0.5 flex items-center gap-3 text-[11px] text-slate-300">
                <span>{formatSpeed(Math.round(hoverPoint.speed || 0))}</span>
                <span>{Math.round(hoverPoint.heading || 0)}°</span>
                {hoverPoint.satellites != null && <span>sat {hoverPoint.satellites}</span>}
              </div>
              <div className="mt-0.5 text-[10px] text-slate-500">
                {Number(hoverPoint.lat).toFixed(5)}, {Number(hoverPoint.lng).toFixed(5)} · {deviceTz}
              </div>
            </div>
          )}

          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm z-[1000]" data-testid="event-trail-map-loading">
              <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
              <span className="ml-2 text-sm text-slate-200">Loading trail…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="absolute top-3 right-3 bg-red-900/90 text-red-100 text-xs px-3 py-1.5 rounded z-[1000]" data-testid="event-trail-map-error">
              {error}
            </div>
          )}

          {/* Empty state — show clearly when trail has no points */}
          {!loading && !error && points.length === 0 && (
            <div className="absolute top-3 right-3 bg-amber-900/90 text-amber-100 text-xs px-3 py-1.5 rounded z-[1000]" data-testid="event-trail-map-empty">
              No GPS history in ±{windowMinutes} min · device may have been off-line
            </div>
          )}

          {/* Speed legend — pinned to bottom-left, z-index above Leaflet attribution */}
          <div className="absolute bottom-3 left-3 bg-slate-900/95 backdrop-blur rounded px-2.5 py-1.5 text-[10px] text-slate-200 border border-slate-700 flex items-center gap-2 z-[1000] shadow-lg" data-testid="event-trail-speed-legend">
            <span className="opacity-70">Speed:</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#3b82f6' }} />0</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#22c55e' }} />30</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#eab308' }} />60</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#f97316' }} />90</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />100+</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTrailMap;
